import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule, ToastController } from '@ionic/angular';
import { BerService } from '../ber.service';
import { BER_OPCIOK } from '../ber.opciok';
import { AdatszolgApi, evLista, HONAPNEV } from './adatszolg.api';
import { adatTabla, alairas, datum, dokumentumNyomtat, esc, felDoboz, ft, nemHivatalos } from './dokumentum-nyomtatas';
import { Router } from '@angular/router';
import { foJogviszony, iratLapok, IratTetel, KATALOGUS } from './ber-iratok';
import { berjegyzekLap, berjegyzekOpciok, berjegyzekOpciokMent } from './berjegyzek';

/*  BÉR – Nyomtatványok (RLB: Bér adatszolgáltatás → Nyomtatványok). Dolgozónkénti, nyomtatható
 *  dokumentumok a TÁROLT számfejtésekből:
 *
 *   - ÉVES JÖVEDELEMIGAZOLÁS – a NAV M30 nyomtatvány TELJES adattartalmával és szerkezetével: a
 *     sorszámok, oszlopok és részek (I–VI.) a NAV 25M30 ÁNYK-sablonjából valók. A 26M30 a NAV-nál
 *     jellemzően az év végén jelenik meg; addig a 2026-tól új kedvezmény (két gyermeket nevelő anyák)
 *     külön, sorszám nélkül szerepel.
 *   - MUNKÁLTATÓI IGAZOLÁS FENNÁLLÓ MUNKAVISZONYRÓL – jogviszony, munkaidő, alapbér, az utolsó 6 hónap
 *     keresete átlaggal, fennálló letiltások (hitelkérelemhez, bérléshez, hatósághoz).
 *   - IGAZOLÁS A MUNKAVISZONY MEGSZŰNÉSEKOR – belső minta (Mt. 80. § (2)): jogviszony, a megszűnés
 *     évében kiadott szabadság és betegszabadság, fennálló letiltások, utolsó 12 hónap járulékalapja.
 *     A jogszabályi adattartalom ellenőrizendő; a hivatalos TB-/álláskeresési igazolásokat nem váltja ki.
 *   - HAVI BÉRJEGYZÉK – a Béradatok képernyő bérlapja (ber.js /berlap), itt egy nyomtatásban a hónap
 *     összes dolgozójára.
 */
type Lap = 'katalogus' | 'igazolas' | 'munkaltatoi' | 'kilepo' | 'berjegyzek';

/*  Az M30 I. részének sorai a nyomtatvány szerint: sorszám, megnevezés, és hogy a b) bevétel /
 *  c) költség oszlop kitölthető-e (a többi sor csak a d) jövedelmet kéri). */
const M30_I: [string, string, boolean][] = [
  ['1', 'Munkaviszonyból származó bérjövedelem az érdekképviseleti tagdíj nélkül', false],
  ['2', 'Munkaviszonnyal kapcsolatos költségtérítés', true],
  ['3', 'Külszolgálatért kapott jövedelem', true],
  ['4', 'Más bérjövedelem', false],
  ['5', 'Más nem önálló tevékenységből származó jövedelmek', false],
  ['6', 'Nem önálló tevékenységgel kapcsolatos költségtérítés', true],
  ['7', 'Önálló tevékenységből származó jövedelem (708–714. sorok összege)', true],
  ['708', '– A 7. sorból az ingatlan-bérbeadásból származó jövedelem', true],
  ['709', '– A 7. sorból az európai parlamenti képviselő e tevékenységéből származó jövedelme', true],
  ['710', '– A 7. sorból a helyi önkormányzati képviselő e tevékenységéből származó jövedelme', true],
  ['711', '– A 7. sorból a választott könyvvizsgáló e tevékenységéből származó jövedelme', true],
  ['712', '– A 7. sorból a magánszemély által nem egyéni vállalkozóként kötött, díjazás ellenében történő munkavégzésre irányuló más szerződés alapján folytatott tevékenységből származó jövedelem', true],
  ['713', '– A 7. sorból a szálláshely-szolgáltatásból származó jövedelem', true],
  ['714', '– A 7. sorból a minden más, a 708–713. sorokban nem szereplő önálló tevékenységből származó jövedelem', true],
  ['18', 'Egyéb jogcímen kapott jövedelem', true],
  ['20', 'Külföldön adózott, de a kettős adóztatást kizáró egyezmény alapján a belföldi adóalapba is beszámítandó jövedelem', true],
  ['21', 'Külföldön adózott, de a kettős adóztatást kizáró egyezmény hiányában belföldön is adóköteles jövedelem', true],
];
const NEM_HIVATALOS_KILEPO = 'Belső minta, nem hivatalos nyomtatvány. A munkaviszony megszűnésekor kiadandó igazolások '
  + 'adattartalmát (Mt. 80. § (2), Tbj.) a kiállítás előtt ellenőrizni kell; a hatósági formanyomtatványokat nem váltja ki.';

@Component({
  selector: 'app-ber-nyomtatvanyok',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './nyomtatvanyok.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', '../ber-shared.scss', './adatszolg.component.scss'],
})
export class NyomtatvanyokComponent implements OnInit {
  evek = evLista();
  honapok = BER_OPCIOK.honapok;
  fulek: { k: Lap; c: string }[] = [
    { k: 'katalogus', c: 'Nyomtatványok' },
    { k: 'igazolas', c: 'Éves jövedelemigazolás (M30)' },
    { k: 'munkaltatoi', c: 'Munkáltatói igazolás (fennálló munkaviszony)' },
    { k: 'kilepo', c: 'Igazolás a munkaviszony megszűnésekor' },
    { k: 'berjegyzek', c: 'Havi bérjegyzék' },
  ];
  lap: Lap = 'katalogus';
  /*  KATALÓGUS (ber-iratok.ts): pipált iratok × kijelölt dolgozók, egy nyomtatásban. */
  readonly katalogus = KATALOGUS;
  kijeloltIratok = new Set<string>();
  kijeloltDolgozok = new Set<string>();
  dolgozoKereso = '';
  csakAktivDolgozo = true;
  katalogusFut = '';
  tolt = false;

  igazolas: any = null;             // /jovedelemigazolas válasza
  alkalmazottak: any[] = [];        // /nyomtatvanyok/alkalmazottak
  csakMegszunt = true;
  berlista: any = { sorok: [] };    // ber.js /berlista

  constructor(private ber: BerService, private api: AdatszolgApi, private toast: ToastController, private router: Router) {}

  get aktiv() { return this.ber.aktiv; }

  async ngOnInit() { await this.ber.init(); await this.betolt(); }

  async lapValt(l: Lap) { this.lap = l; await this.betolt(); }
  async evValt(ev: number) { await this.ber.setAktiv({ ev }); await this.betolt(); }
  async hoValt(ho: number) { await this.ber.setAktiv({ ho }); await this.betolt(); }

  async betolt() {
    if (!this.aktiv.ugyfelId) return;
    this.tolt = true;
    try {
      const id = this.aktiv.ugyfelId;
      if (this.lap === 'igazolas') this.igazolas = await this.api.get(`/jovedelemigazolas/${id}?ev=${this.aktiv.ev}`);
      else if (this.lap === 'kilepo' || this.lap === 'munkaltatoi' || this.lap === 'katalogus') this.alkalmazottak = await this.api.get(`/nyomtatvanyok/alkalmazottak/${id}?ev=${this.aktiv.ev}`);
      else this.berlista = await this.ber.get('/berlista/' + id + this.ber.evhoq());
    } catch (e: any) { this.hiba(this.api.hibaUzenet(e, 'Nem sikerült betölteni.')); }
    finally { this.tolt = false; }
  }

  /* ─────────────────────────── KATALÓGUS ─────────────────────────── */

  /** Aktív: van az évben élő jogviszonya (vagy nincs inaktívnak jelölve, és nincs lezárt jogviszonya). */
  private dolgozoAktiv(a: any): boolean {
    if (+a.inaktiv) return false;
    const ev = this.aktiv.ev;
    return (a.jogviszonyok || []).some((j: any) => String(j.kezdet || '').slice(0, 10) <= `${ev}-12-31` && (!j.veg || String(j.veg).slice(0, 10) >= `${ev}-01-01`));
  }
  get lathatoDolgozok(): any[] {
    const k = this.dolgozoKereso.trim().toLowerCase();
    return this.alkalmazottak.filter((a) => (!this.csakAktivDolgozo || this.dolgozoAktiv(a))
      && (!k || `${a.nev} ${a.adoazonosito || ''} ${a.taj || ''}`.toLowerCase().includes(k)));
  }
  dolgozoValt(a: any, be: boolean) { be ? this.kijeloltDolgozok.add(a.id) : this.kijeloltDolgozok.delete(a.id); }
  mindDolgozo(be: boolean) { for (const a of this.lathatoDolgozok) this.dolgozoValt(a, be); }
  iratValt(t: IratTetel, be: boolean) { be ? this.kijeloltIratok.add(t.kod) : this.kijeloltIratok.delete(t.kod); }
  get vanHaviIrat(): boolean { return this.katalogus.some((c) => c.tetelek.some((t) => t.havi && this.kijeloltIratok.has(t.kod))); }
  navNyit(t: IratTetel) { this.router.navigate(['/ber/bevallasok'], { queryParams: { kod: t.nav } }); }

  /*  A pipált iratok a kijelölt dolgozókra, dolgozónként egymás után, a katalógus sorrendjében. A dossziéból
   *  készülők egy hívással jönnek; a meglévő igazolások (M30, munkáltatói, kilépő, bérjegyzék) a saját
   *  végpontjukról, a dolgozó évben élő (kilépőnél a lezárt) jogviszonyával. */
  async katalogusNyomtat() {
    const tetelek = this.katalogus.flatMap((c) => c.tetelek).filter((t) => this.kijeloltIratok.has(t.kod) && (t.fajta === 'irat' || t.fajta === 'meglevo'));
    const dolgozok = this.alkalmazottak.filter((a) => this.kijeloltDolgozok.has(a.id));
    if (!tetelek.length || !dolgozok.length) return;
    const id = this.aktiv.ugyfelId!; const ev = this.aktiv.ev; const ho = this.aktiv.ho;
    const kihagyva: string[] = [];
    this.katalogusFut = 'Adatok betöltése…';
    try {
      const kell = (k: string) => tetelek.some((t) => t.kod === k);
      const dossz: any = tetelek.some((t) => t.fajta === 'irat')
        ? await this.api.get(`/dosszie/${id}?ev=${ev}${tetelek.some((t) => t.tortenet) ? '&tortenet=1' : ''}&alkalmazott=${encodeURIComponent(dolgozok.map((a) => a.id).join(','))}`) : null;
      const m30: any = kell('m30') ? await this.api.get(`/jovedelemigazolas/${id}?ev=${ev}`) : null;
      const berlista: any = kell('berjegyzek') ? await this.ber.get('/berlista/' + id + this.ber.evhoq()) : null;
      const lapok: string[] = [];
      for (const [i, a] of dolgozok.entries()) {
        this.katalogusFut = `${i + 1}/${dolgozok.length}: ${a.nev}`;
        const d = dossz?.dolgozok?.find((x: any) => x.alkalmazott.id === a.id);
        for (const t of tetelek) {
          if (t.fajta === 'irat') { if (d) lapok.push(...iratLapok(t.kod, dossz.ugyfel, d, ev, ho)); continue; }
          if (t.kod === 'm30') {
            const ig = m30?.igazolasok?.find((x: any) => x.alkalmazott?.id === a.id);
            if (ig) lapok.push(this.igazolasLap(m30.ugyfel, ig, m30.ev)); else kihagyva.push(`${a.nev}: M30 (${ev}-ben nincs számfejtés)`);
          } else if (t.kod === 'berjegyzek') {
            const sorok = (berlista?.sorok || []).filter((x: any) => x.alkalmazott_id === a.id);
            for (const x of sorok) lapok.push(this.berjegyzekLap(await this.ber.get('/berlap/' + x.id)));
            if (!sorok.length) kihagyva.push(`${a.nev}: bérjegyzék (${ev}. ${HONAPNEV[ho - 1]} nincs számfejtés)`);
          } else if (t.kod === 'munkaltatoi') {
            const j = foJogviszony(a, ev);
            if (j) lapok.push(this.munkaltatoiLap(await this.api.get(`/munkaltatoi/${id}?jogviszony_id=${encodeURIComponent(j.id)}`)));
            else kihagyva.push(`${a.nev}: munkáltatói igazolás (nincs jogviszony)`);
          } else if (t.kod === 'kilepo') {
            const j = (a.jogviszonyok || []).filter((x: any) => x.veg).sort((x: any, y: any) => String(y.veg).localeCompare(String(x.veg)))[0];
            if (j) lapok.push(this.kilepoLap(await this.api.get(`/kilepo/${id}?jogviszony_id=${encodeURIComponent(j.id)}`)));
            else kihagyva.push(`${a.nev}: kilépő igazolás (nincs megszűnt jogviszony)`);
          }
        }
      }
      if (lapok.length) dokumentumNyomtat(`Nyomtatványok – ${dolgozok.length} dolgozó`, lapok);
      if (kihagyva.length) this.figyelmeztet(`Kimaradt: ${kihagyva.slice(0, 4).join('; ')}${kihagyva.length > 4 ? ` és még ${kihagyva.length - 4}` : ''}.`);
      else if (!lapok.length) this.figyelmeztet('Nincs nyomtatható irat a kijelöléshez.');
    } catch (e: any) { this.hiba(this.api.hibaUzenet(e, 'A nyomtatványok nem készültek el.')); }
    finally { this.katalogusFut = ''; }
  }
  private async figyelmeztet(m: string) { (await this.toast.create({ message: m, duration: 6000, color: 'warning' })).present(); }

  get honapCim(): string { return HONAPNEV[this.aktiv.ho - 1] || ''; }
  kedvOssz(ig: any): number { return (ig.sorok.kedvezmenyek || []).reduce((x: number, k: any) => x + Number(k.osszeg || 0), 0); }

  /** A kilépő igazoláshoz választható jogviszonyok (alapból csak a megszűntek, az évben). */
  get jogviszonyok(): any[] {
    const ev = String(this.aktiv.ev);
    const l: any[] = [];
    for (const a of this.alkalmazottak) {
      for (const j of a.jogviszonyok || []) {
        if (j.jogviszony_tipus === 'egyeni_vallalkozo') continue;
        if (this.csakMegszunt && !(j.veg && String(j.veg).startsWith(ev))) continue;
        l.push({ ...j, alkalmazott_nev: a.nev, adoazonosito: a.adoazonosito });
      }
    }
    return l.sort((x, y) => String(y.veg || '9999').localeCompare(String(x.veg || '9999')));
  }

  /** A munkáltatói igazoláshoz: a fennálló (még meg nem szűnt) jogviszonyok. */
  get fennalloJogviszonyok(): any[] {
    const ma = new Date().toISOString().slice(0, 10);
    const l: any[] = [];
    for (const a of this.alkalmazottak) {
      for (const j of a.jogviszonyok || []) {
        if (j.jogviszony_tipus === 'egyeni_vallalkozo') continue;
        if (j.veg && String(j.veg).slice(0, 10) < ma) continue;
        l.push({ ...j, alkalmazott_nev: a.nev, adoazonosito: a.adoazonosito });
      }
    }
    return l.sort((x, y) => String(x.alkalmazott_nev).localeCompare(String(y.alkalmazott_nev), 'hu'));
  }

  /* ─────────────────────────── JÖVEDELEMIGAZOLÁS ─────────────────────────── */

  /*  AZ M30 LAPJA – a NAV nyomtatvány részei, sorszámai és oszlopai szerint. Üres (nulla) sor is
   *  szerepel, ahogy a nyomtatványon: a magánszemély így ugyanabban a rendben látja, mint a
   *  bevallás-tervezetében. */
  private igazolasLap(u: any, ig: any, ev: number): string {
    const a = ig.alkalmazott; const m = ig.m30 || {}; const I = m.I || {};
    const ures = (v: any) => (Number(v) ? ft(v) : '');
    const nev2 = (n: string) => { const r = String(n || '').trim().split(/\s+/); return [r[0] || '', r.slice(1).join(' ')]; };
    const [csn, un] = nev2(a.nev); const [szcsn, szun] = nev2(a.szuletesi_nev || a.nev);
    const kod = m.nyomtatvany || `${String(ev).slice(-2)}M30`;
    const sablon26 = ev >= 2026;
    const Isor = ([sor, cim, bc]: [string, string, boolean]) => {
      const r = I[sor] || {};
      return `<tr><td class="num">${esc(sor)}.</td><td>${esc(cim)}</td><td class="num">${bc ? ures(r.b) : ''}</td><td class="num">${bc ? ures(r.c) : ''}</td><td class="num">${ures(r.d)}</td></tr>`;
    };
    const III: [string, string, any][] = [
      ['605', 'Az adóelőleg megállapításánál figyelembe vett három gyermeket nevelő anyák kedvezménye', m.III?.['605']],
      ['606', 'Az adóelőleg megállapításánál figyelembe vett négy vagy több gyermeket nevelő anyák kedvezménye', m.III?.['606']],
      ['607', 'Az adóelőleg megállapításánál figyelembe vett csecsemőgondozási díj, gyermekgondozási díj, örökbefogadói díj kedvezménye', null],
      ['608', 'Az adóelőleg megállapításánál figyelembe vett 25 év alatti fiatalok kedvezménye', m.III?.['608']],
      ['609', 'Az adóelőleg megállapításánál figyelembe vett 30 év alatti anyák kedvezménye', m.III?.['609']],
      ['610', 'Az adóelőleg megállapításánál figyelembe vett súlyos fogyatékosság miatti kedvezmény (személyi kedvezmény)', m.III?.['610']],
      ['611', 'Az adóelőleg megállapításánál figyelembe vett első házasok kedvezményének összege', m.III?.['611']],
      ['612', 'Az adóelőleg megállapításánál figyelembe vett családi kedvezmény összege', m.III?.['612']],
      ['55', 'A bevételből levont érdekképviseleti tagdíj', null],
      ['56', `A ${ev}-ben kifizetett osztalékelőleg / az osztalékelőlegből levont adó`, null],
      ['57', 'Külföldön adózott és a kettős adóztatást kizáró egyezmény alapján belföldön adómentes jövedelem', null],
    ];
    const van2 = Number(m.III?.anya2) > 0 || sablon26;
    const IVfej = ['Hónap', 'a) Összevont adóalapba tartozó jövedelem', 'b) Kedvezményalap', 'c) Három gyermeket nevelő anyák kedv.', 'd) Négy vagy több gyermeket nevelő anyák kedv.', 'e) 25 év alatti fiatalok kedv.', 'f) 30 év alatti anyák kedv.'];
    if (van2) IVfej.push('Két gyermeket nevelő anyák kedv. (2026-tól)');
    const IVsor = (h: any, cim: string) => { const r = [cim, ures(h.a), ures(h.b), ures(h.c), ures(h.d), ures(h.e), ures(h.f)]; if (van2) r.push(ures(h.anya2)); return r; };
    return `
      <div class="fej"><div><h1>A munkáltató, kifizető összesített igazolása a ${ev}. évi személyi jövedelemadó bevallásához</h1>
        <div class="alcim">a NAV ${esc(kod)} nyomtatvány adattartalmával</div></div>
        <div class="jobb"><b style="font-size:14pt">${esc(kod)}</b></div></div>
      <div class="felek">
        ${felDoboz('Munkáltató, kifizető', [['Neve', u.hivatalos_nev || u.nev], ['Adószáma', u.adoszam], ['Címe', u.cim]])}
        ${felDoboz('Magánszemély adatai', [['Adóazonosító jele', a.adoazonosito], ['Családi neve, utóneve', [a.titulus, csn, un].filter(Boolean).join(' ')],
          ['Születési családi neve, utóneve', [szcsn, szun].filter(Boolean).join(' ')],
          ['Születésének időpontja, helye', [a.szul_ido ? datum(a.szul_ido) : '', a.szul_hely].filter(Boolean).join(', ')],
          ['Anyja születési családi és utóneve', a.anyja_neve], ['Címe', a.cim]])}
      </div>
      <h2>I. Az összevont adóalapba tartozó jövedelmek (bevételek)</h2>
      <table class="racs"><thead><tr><th class="num">Sor</th><th>Megnevezés</th><th class="num">b) Bevétel</th><th class="num">c) Költség</th><th class="num">d) Jövedelem</th></tr></thead>
        <tbody>${M30_I.map(Isor).join('')}
          <tr class="osszesen"><td class="num">22.</td><td>Az összevont adóalapba tartozó jövedelmek összege (az 1–7. és a 18–21. sorok „d” oszlopainak együttes összege)</td><td></td><td></td><td class="num">${ft(m['22'])}</td></tr>
          <tr class="osszesen"><td class="num">65.</td><td>Levont adóelőleg összege</td><td class="num">${ft(m['65'])}</td><td></td><td></td></tr>
        </tbody></table>
      <h2>II. Elkülönülten adózó jövedelmek</h2>
      ${adatTabla(['Sor', 'Megnevezés', 'b) Bruttó összeg', 'c) Adóalapot terhelő adó', 'd) Adó alapja', 'e) Levont adó'], [
        ['165.', 'Árfolyamnyereségből származó jövedelem és adója', '', '', '', ''],
        ['167.', 'Osztalék címén megszerzett jövedelem és adója', '', '', '', ''],
        ['173.', 'A vállalkozásból kivont jövedelem és adója', '', '', '', ''],
        ['175.', 'Egyéb forrásadós jövedelmek és ezek adója', '', '', '', ''],
        ['176.', 'A külön adózó jövedelmek és adójuk összege', '', '', '', ''],
        ['67.', 'Levont (forrás)adó összege', '', '', '', ''],
      ], [2, 3, 4, 5])}
      <h2>III. Egyéb adatok</h2>
      ${adatTabla(['Sor', 'Megnevezés', 'Összeg (Ft)'], [
        ...III.map(([sor, cim, v]) => [`${sor}.`, cim, ures(v)]),
        ...(van2 ? [['–', `Az adóelőleg megállapításánál figyelembe vett két gyermeket nevelő anyák kedvezménye (2026-tól; sorszáma a ${kod} megjelenésekor)`, ures(m.III?.anya2)]] : []),
      ], [2])}
      <h2>IV. Kedvezményekkel kapcsolatos tájékoztatás</h2>
      <div class="megj">Három és négy vagy több gyermeket nevelő anyák, 25 év alatti fiatalok, 30 év alatti anyák kedvezményének szempontjából figyelembe vehető jövedelem (kedvezményalap) és az évközben érvényesített kedvezmény összege.</div>
      ${adatTabla(IVfej, (m.IV || []).map((h: any) => IVsor(h, HONAPNEV[h.ho - 1])), IVfej.map((_, i) => i).slice(1), IVsor(m.IV_ossz || {}, 'Összesen'))}
      <h2>V. Családi járulékkedvezménnyel kapcsolatos tájékoztatás</h2>
      ${adatTabla(['Hónap', 'a) Járulékalapot képező jövedelem', 'b) Figyelembe vehető megállapított járulék', 'c) Évközben érvényesített családi járulékkedvezmény', 'd) Levont járulékok összege'],
        (m.V || []).map((h: any) => [HONAPNEV[h.ho - 1], ures(h.a), ures(h.b), ures(h.c), ures(h.d)]), [1, 2, 3, 4],
        ['Összesen', ures(m.V_ossz?.a), ures(m.V_ossz?.b), ures(m.V_ossz?.c), ures(m.V_ossz?.d)])}
      <h2>VI. Szociális hozzájárulási adóval kapcsolatos tájékoztatás</h2>
      ${adatTabla(['Sor', 'Megnevezés', 'Összeg (Ft)'], [
        ['286.', 'A Szocho tv. 1. § (1)–(3) bekezdés szerinti összevont adóalapba tartozó adóalap', ures(m.VI?.['286'])],
        ['287.', 'A Szocho tv. 1. § (5) bekezdés a)–d) pontjai szerinti külön adózó jövedelmek összege', ''],
        ['289.', 'Kifizető által a magánszemély Szocho tv. 1. § (5) bekezdés a)–d) pontjai szerinti jövedelme után megfizetett szocho', ''],
      ], [2])}
      ${ig.tervezet_db ? `<div class="megj">${ig.tervezet_db} havi számfejtés még tervezet (nem véglegesített).</div>` : ''}
      ${sablon26 ? `<div class="megj">A ${esc(kod)} nyomtatványt a NAV még nem tette közzé; a sorszámok és oszlopok a 25M30 szerintiek, a 2026-tól bevezetett kedvezmény külön sorban szerepel.</div>` : ''}
      ${alairas(u.telepules || '')}
      <div class="atvetel"><div class="vonal">Az igazolást a munkáltatótól/kifizetőtől átvettem.</div></div>`;
  }

  nyomtatIgazolas(ig?: any) {
    const d = this.igazolas;
    if (!d?.igazolasok?.length) return;
    const lista = ig ? [ig] : d.igazolasok;
    dokumentumNyomtat(`Jövedelemigazolás ${d.ev}`, lista.map((x: any) => this.igazolasLap(d.ugyfel, x, d.ev)));
  }

  /* ─────────────────── MUNKÁLTATÓI IGAZOLÁS (FENNÁLLÓ MUNKAVISZONY) ─────────────────── */

  private munkaltatoiLap(d: any): string {
    const u = d.ugyfel; const a = d.alkalmazott; const j = d.jogviszony;
    const berTipus = j.ber_tipus === 'ora' ? 'órabér' : 'havi alapbér';
    const munkarend = j.munkarend === 'nincs' ? 'munkaidő nélkül' : (j.munkarend === 'egyenlotlen' ? 'egyenlőtlen munkaidő-beosztás' : 'általános munkarend');
    const jelol = '☐ igen   ☐ nem';
    return `
      <div class="fej"><div><h1>Munkáltatói igazolás</h1>
        <div class="alcim">fennálló ${esc((j.tipus_nev || 'munkaviszony').toLowerCase())}ról és a keresetről</div></div>
        <div class="jobb"><b>${esc(a.nev)}</b></div></div>
      <div class="felek">
        ${felDoboz('Munkáltató', [['Név', u.hivatalos_nev || u.nev], ['Adószám', u.adoszam], ['Székhely', u.cim]])}
        ${felDoboz('Munkavállaló', [['Név', a.nev], ['Születési név', a.szuletesi_nev], ['Anyja neve', a.anyja_neve],
          ['Születési hely, idő', [a.szul_hely, a.szul_ido ? datum(a.szul_ido) : ''].filter(Boolean).join(', ')],
          ['Adóazonosító jel', a.adoazonosito], ['TAJ', a.taj], ['Lakcím', a.cim]])}
      </div>
      <p>Igazoljuk, hogy a fenti munkavállaló társaságunknál a kiállítás napján <b>fennálló ${esc((j.tipus_nev || 'munkaviszony').toLowerCase())}ban</b> áll.</p>
      <h2>1. A jogviszony adatai</h2>
      ${adatTabla(['Adat', 'Érték'], [
        ['A jogviszony típusa', j.tipus_nev || '—'],
        ['A jogviszony kezdete', datum(j.kezdet)],
        ['Időtartama', j.hatarozott ? `határozott, ${datum(j.veg)}-ig` : 'határozatlan'],
        ['Munkakör (FEOR)', [j.munkakor, j.feor ? `FEOR ${j.feor}` : ''].filter(Boolean).join(' – ') || '—'],
        ['Munkaidő', j.munkarend === 'nincs' ? 'munkaidő nélkül' : `heti ${j.heti_oraszam} óra, ${munkarend}`],
        [`Alapbér (${berTipus})`, j.alapber ? `${ft(j.alapber)} Ft${j.ber_tipus === 'ora' ? '/óra' : '/hó'}` : '—'],
        ['Próbaidő alatt áll', jelol],
        ['Felmondási idő alatt áll', jelol],
        ['Megszüntetése folyamatban van', jelol],
      ])}
      <h2>2. A kereset az utolsó ${d.honapok.length || 6} hónapban</h2>
      ${adatTabla(['Hónap', 'Bruttó kereset (Ft)', 'Levonások (Ft)', 'ebből letiltás (Ft)', 'Nettó kereset (Ft)'],
        d.honapok.map((h: any) => [`${h.ev}. ${HONAPNEV[h.ho - 1]}`, ft(h.brutto), ft(h.levonas), h.letiltas ? ft(h.letiltas) : '', ft(h.netto)]), [1, 2, 3, 4],
        d.honapok.length ? ['Havi átlag', ft(d.atlag_brutto), '', '', ft(d.atlag_netto)] : undefined)}
      <h2>3. A munkabért terhelő letiltások</h2>
      ${d.letiltasok.length
        ? adatTabla(['Jogosult', 'Ügyszám', 'Jogcím', 'Mérték', 'Fennálló tartozás (Ft)'], d.letiltasok.map((l: any) => [l.jogosult || '—', l.ugyszam || '—', l.tipus,
          l.szazalek != null ? `${l.szazalek}%` : (l.fix_osszeg != null ? `${ft(l.fix_osszeg)} Ft/hó` : '—'), l.hatralek != null ? ft(l.hatralek) : '—']), [4])
        : '<div class="megj">A munkavállaló munkabérét letiltás nem terheli.</div>'}
      ${d.figyelmeztetesek?.length ? `<ul class="megj">${d.figyelmeztetesek.map((m: string) => `<li>${esc(m)}</li>`).join('')}</ul>` : ''}
      <div class="megj">Az igazolást a munkavállaló kérésére, a bérszámfejtés tárolt adataiból állítottuk ki. A „☐ igen ☐ nem” sorokat a kiállító jelöli.</div>
      ${alairas(u.telepules || '')}`;
  }

  async nyomtatMunkaltatoi(j: any) {
    try {
      const d = await this.api.get(`/munkaltatoi/${this.aktiv.ugyfelId}?jogviszony_id=${encodeURIComponent(j.id)}`);
      dokumentumNyomtat(`Munkáltatói igazolás – ${j.alkalmazott_nev}`, [this.munkaltatoiLap(d)]);
    } catch (e: any) { this.hiba(this.api.hibaUzenet(e, 'A munkáltatói igazolás nem készült el.')); }
  }

  /* ─────────────────────── KILÉPŐ (MEGSZŰNÉSI) IGAZOLÁS ─────────────────────── */

  private kilepoLap(d: any): string {
    const u = d.ugyfel; const a = d.alkalmazott; const j = d.jogviszony; const jel = d.jelenlet;
    const let$ = d.letiltasok.map((l: any) => [l.jogosult || '—', l.ugyszam || '—', l.tipus,
      l.szazalek != null ? `${l.szazalek}%` : (l.fix_osszeg != null ? `${ft(l.fix_osszeg)} Ft/hó` : '—'),
      l.hatralek != null ? ft(l.hatralek) : '—']);
    const ja = d.jarulekalap;
    const jaOssz = (k: string) => ja.reduce((x: number, r: any) => x + Number(r[k] || 0), 0);
    return `
      <div class="fej"><div><h1>Igazolás a munkaviszony megszűnésekor</h1>
        <div class="alcim">${esc(j.tipus_nev || '')} · megszűnés: ${esc(datum(j.veg))}</div></div>
        <div class="jobb"><b>${esc(a.nev)}</b></div></div>
      ${nemHivatalos(NEM_HIVATALOS_KILEPO)}
      <div class="felek">
        ${felDoboz('Munkáltató', [['Név', u.hivatalos_nev || u.nev], ['Adószám', u.adoszam], ['Székhely', u.cim]])}
        ${felDoboz('Munkavállaló', [['Név', a.nev], ['Születési név', a.szuletesi_nev], ['Anyja neve', a.anyja_neve],
          ['Születési hely, idő', [a.szul_hely, a.szul_ido ? datum(a.szul_ido) : ''].filter(Boolean).join(', ')],
          ['Adóazonosító jel', a.adoazonosito], ['TAJ', a.taj], ['Lakcím', a.cim]])}
      </div>
      <h2>1. A munkaviszony adatai</h2>
      ${adatTabla(['Adat', 'Érték'], [
        ['A jogviszony típusa', j.tipus_nev || '—'],
        ['Munkakör (FEOR)', [j.munkakor, j.feor ? `FEOR ${j.feor}` : ''].filter(Boolean).join(' – ') || '—'],
        ['A munkaviszony kezdete', datum(j.kezdet)],
        ['A munkaviszony megszűnésének napja', datum(j.veg)],
        ['A megszűnés módja (jogcíme)', j.kilepes_ok || '— (kitöltendő)'],
        ['Heti munkaidő', `${j.heti_oraszam} óra`],
      ])}
      <h2>2. A ${d.ev}. évben kiadott szabadság és igénybe vett betegszabadság</h2>
      ${adatTabla(['Megnevezés', 'Nap', 'Óra'], [
        ['Kiadott szabadság', String(jel.szabadsag_nap), String(jel.szabadsag_orak)],
        ['Igénybe vett betegszabadság', String(jel.betegszabadsag_nap), String(jel.betegszabadsag_orak)],
        ['Táppénzes napok', String(jel.tappenz_napok), ''],
      ], [1, 2])}
      <div class="megj">A napok a jelenléti adatokból, ${esc(j.napi_oraszam)} órás munkanappal számolva. A betegszabadság napjai az új munkáltatónál a még igénybe vehető betegszabadság megállapításához kellenek.</div>
      <h2>3. A munkavállalót terhelő, fennálló letiltások</h2>
      ${d.letiltasok.length
        ? adatTabla(['Jogosult', 'Ügyszám', 'Jogcím', 'Mérték', 'Fennálló tartozás (Ft)'], let$, [4])
        : '<div class="megj">A munkavállalót letiltás nem terheli.</div>'}
      ${d.levont_letiltas_ev ? `<div class="megj">A ${d.ev}. évben a munkabérből levont letiltások összesen: <b>${ft(d.levont_letiltas_ev)} Ft</b>.</div>` : ''}
      <h2>4. Járulékalapot képező jövedelem a megszűnést megelőző (legfeljebb 12) hónapban</h2>
      ${adatTabla(['Hónap', 'Bruttó (Ft)', 'Járulékalap (Ft)', 'Levont TB-járulék (Ft)'],
        ja.map((r: any) => [`${r.ev}. ${HONAPNEV[r.ho - 1]}`, ft(r.brutto), ft(r.jarulekalap), ft(r.tb_levont)]), [1, 2, 3],
        ja.length ? ['Összesen', ft(jaOssz('brutto')), ft(jaOssz('jarulekalap')), ft(jaOssz('tb_levont'))] : undefined)}
      ${d.figyelmeztetesek?.length ? `<ul class="megj">${d.figyelmeztetesek.map((m: string) => `<li>${esc(m)}</li>`).join('')}</ul>` : ''}
      ${alairas()}
      <div class="atvetel"><div class="vonal">az igazolást átvettem (munkavállaló)</div></div>`;
  }

  async nyomtatKilepo(j: any) {
    try {
      const d = await this.api.get(`/kilepo/${this.aktiv.ugyfelId}?jogviszony_id=${encodeURIComponent(j.id)}`);
      dokumentumNyomtat(`Kilépő igazolás – ${j.alkalmazott_nev}`, [this.kilepoLap(d)]);
    } catch (e: any) { this.hiba(this.api.hibaUzenet(e, 'A kilépő igazolás nem készült el.')); }
  }

  /* ─────────────────────────── HAVI BÉRJEGYZÉK ─────────────────────────── */

  /*  BÉRJEGYZÉK – a közös, részletes lap (berjegyzek.ts), ugyanaz, mint a Számfejtés „Bérlap” gombja. */
  private berjegyzekLap(d: any): string { return berjegyzekLap(d, this.bjOpciok); }
  /** A bérjegyzék kapcsolható blokkjai (naptár, munkáltatói közterhek) – alapból mind be. */
  bjOpciok = berjegyzekOpciok();
  bjOpciokMent() { berjegyzekOpciokMent(this.bjOpciok); }

  async nyomtatBerjegyzek(r?: any) {
    const sorok = r ? [r] : (this.berlista.sorok || []);
    if (!sorok.length) return;
    try {
      const lapok: string[] = [];
      for (const x of sorok) lapok.push(this.berjegyzekLap(await this.ber.get('/berlap/' + x.id)));
      dokumentumNyomtat(`Bérjegyzék ${this.aktiv.ev}. ${HONAPNEV[this.aktiv.ho - 1]}`, lapok);
    } catch (e: any) { this.hiba(this.api.hibaUzenet(e, 'A bérjegyzék nem készült el.')); }
  }

  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 3000, color: 'danger' })).present(); }
}
